import apiClient from "../axios";

export enum CompanyRegistationStep {
  Unknown = 0,
  ETaxes = 1,
  AddDocuments = 2,
  AddUsers = 3,
}

export interface ApiResponse<T> {
  responseValue: T;
  statusCode: number;
  message: string;
}

export interface RegisterCompanyRequest {
  voen: string;
  personType: number; // 1 => physical?, 2 => legal? (follow backend contract)
}

// VAT_SIMA doc 2: ƏDV rekviziti (yalnız ƏDV ödəyicisi şirkətdə dolu gəlir)
export interface VatRequisite {
  recipientName?: string;
  recipientVoen?: string;
  account?: string;
  bank?: {
    name?: string;
    code?: string;
    voen?: string;
    correspondentAccount?: string;
    swift?: string;
  };
  paymentPurpose?: string;
  // Yalnız publicInfo-da dolu gəlir; get-infos/get-by-voen-də null
  vatRegistrationDate?: string | null;
}

// VAT_SIMA doc 3: SIMA ilə kim/nə vaxt imzalayıb (təsdiqə qədər null)
export interface SimaConfirmation {
  confirmedAt?: string;
  signerName?: string | null;
  signerFin?: string | null;
  signerCompanyUserId?: string | null;
  organizationVoen?: string | null;
}

// Doc 2 (Approval): hüquqşünas təsdiq statusu
export enum CompanyApprovalStatus {
  Draft = 0, // qeydiyyat bitməyib
  PendingReview = 1, // hüquqşünas yoxlamasını gözləyir
  Approved = 2, // təsdiqlənib
  ReturnedForCorrection = 3, // düzəlişə qaytarılıb
  Blocked = 4, // bloklanıb
}

// Doc 2 (Approval): istifadəçinin bölmələri (area)
export enum CompanyArea {
  Users = 1, // istifadəçi idarəsi (yalnız rəhbər)
  Documents = 2, // sənəd/rekvizit/legitim istifadəçi/profil
  Tenders = 3, // Tenders/web, Suggestions/web
  Contracts = 4, // müqavilələr və əlaqəli bölmələr
}

// Doc 2.2: qeydiyyat mərhələlərinin vəziyyəti
export interface CompanyRegistrationStatus {
  companyId: number;
  voen: string;
  name: string;
  hasDirector: boolean;
  isSimaConfirmed: boolean;
  isProfileComplete: boolean;
  canConfirmWithSima: boolean;
  // Doc 4.4: hüquqşünas təsdiqi ilə bağlı yeni sahələr
  approvalStatus?: number; // CompanyApprovalStatus
  approvalComment?: string | null;
  canSubmitForReview?: boolean; // approval/submit indi çağırıla bilər
  // VAT_SIMA doc 3: SIMA ilə kim imzalayıb
  simaConfirmation?: SimaConfirmation | null;
  stages: { stage: number; isCompleted: boolean }[];
  pendingStages: number[];
}

// Doc 4.1: company-user/my-access — rol, şirkət statusu və indi açıq bölmələr
export interface MyAccessResponse {
  companyId: number;
  voen: string;
  companyUserId: string;
  isOwner: boolean;
  isLegitimateUser: boolean;
  department: number;
  permissionType: number; // 0 None, 1 Tender, 2 Contract, 3 All
  isSimaConfirmed: boolean;
  approvalStatus: number; // CompanyApprovalStatus
  approvalComment: string | null;
  isApproved: boolean;
  roleAreas: number[]; // rolun verdiyi bölmələr (təsdiq nəzərə alınmadan)
  areas: number[]; // indi açıq olan bölmələr (Tenders/Contracts yalnız Approved olanda)
}

// Doc 4.3: təsdiq tarixçəsinin bir sətiri
export interface ApprovalHistoryItem {
  id: number;
  action: number; // 1 Submitted, 2 ChangedAfterApproval, 3 Approved, 4 Returned, 5 Blocked, 6 Unblocked
  fromStatus: number;
  toStatus: number;
  comment: string | null;
  createdDate: string;
  isByCompany: boolean;
  changedBy: string;
}

// Qeydiyyat təsdiq kodu məlumatı (e-poçt/WhatsApp-a göndərilir)
export interface RegistrationVerification {
  emails: string[];
  phones: string[];
  expiresInSeconds: number;
  resendInSeconds: number;
}

// Doc 4.2: VÖEN üzrə dövlət mənbələrindən məlumat (company/register cavabında gəlir)
export interface LocalizedName {
  az?: string;
  ru?: string;
  en?: string;
}
export interface PublicInfoTaxAuthority {
  code?: string;
  name?: LocalizedName;
  taxAuthorityAddress?: string;
}
export interface PublicInfoLegalStatus {
  name?: string;
  legalAddress?: string;
  legitimate?: string | null;
  legalForm?: { code?: string; name?: LocalizedName };
  charterCapital?: number;
  financialYearStart?: string;
  financialYearEnd?: string;
  voenRegisteredAt?: string | null;
  stateRegisteredAt?: string | null;
  extractDate?: string | null;
  taxpayerStatus?: { code?: string; name?: LocalizedName };
  riskyTaxpayer?: boolean;
  debt?: number;
}
export interface PublicInfoTaxpayer {
  name?: string;
  tin?: string;
  type?: string;
  legitimate?: string | null;
  taxAuthority?: PublicInfoTaxAuthority;
  debt?: number;
  active?: boolean;
  vatPayer?: boolean;
  riskyPayer?: boolean;
  taxOrganizationName?: string;
  organizationType?: string;
  amountAzn?: number;
  legalTaxpayerStatus?: PublicInfoLegalStatus;
}
export interface PublicInfoVatPayer {
  registrationDate?: string;
  updateDate?: string;
  representativeName?: string;
  organizationForm?: string;
  legalAddress?: string;
  charterAmount?: number;
}
export interface PublicInfoHistoryItem {
  fullName?: string;
  fullAddress?: string | null;
  chiefName?: string | null;
  charterCapital?: string | null;
  operationDate?: string;
  operationName?: string;
  legalForm?: string | null;
}
export interface PublicInfoBudgetAccount {
  beneficiary?: {
    name?: string;
    code?: string;
    tin?: string;
    offsetAccount?: string;
    swiftBin?: string;
  };
  receiver?: { name?: string; tin?: string; operatingAccount?: string };
  payer?: { tin?: string; name?: string };
}
export interface PublicInfoBlacklistItem {
  voen?: string;
  name?: string;
  reason?: string;
  startDate?: string;
  endDate?: string;
  creationDate?: string;
}
export interface PublicInfoStateTenderWon {
  eventId?: number;
  buyerOrganizationName?: string;
  eventName?: string;
  awardedParticipantName?: string;
  awardedOrganizationPrice?: number;
  finishDate?: string;
}
export interface PublicInfo {
  voen?: string;
  fetchedAt?: string;
  taxpayer?: PublicInfoTaxpayer;
  certificate?: { certificateType?: string };
  debt?: { debt?: number; tasksAndObligations?: string };
  vatPayer?: PublicInfoVatPayer | null;
  risk?: { risky?: boolean; riskyPayerInfoList?: unknown[] };
  history?: PublicInfoHistoryItem[];
  budgetAccounts?: PublicInfoBudgetAccount[];
  procurementBlacklist?: PublicInfoBlacklistItem[];
  isProcurementBlacklisted?: boolean;
  stateTendersWonCount?: number;
  stateTendersWon?: PublicInfoStateTenderWon[];
  unavailableSources?: string[];
  // VAT_SIMA doc 2: ƏDV rekviziti (canlı, vatRegistrationDate ilə)
  vatRequisite?: VatRequisite | null;
}

export interface RegisterCompanyResponse {
  id: number;
  name: string;
  voen: string;
  step: CompanyRegistationStep; // Company registration step enum
  personType: number;
  // Doc 2.1: VÖEN əvvəl yazılıbsa və tam mərhələ vəziyyəti
  isAlreadyRegistered?: boolean;
  registrationStatus?: CompanyRegistrationStatus;
  // Doc 4.2: VÖEN üzrə dövlət mənbələrindən məlumat
  publicInfo?: PublicInfo;
  // VAT_SIMA: adətən publicInfo altında gəlir; top-level fallback üçün
  vatRequisite?: VatRequisite | null;
  // Qeydiyyat kodu axını (saxlanmış məlumat olan şirkət üçün)
  registrationToken?: string | null;
  requiresVerification?: boolean;
  verification?: RegistrationVerification | null;
  taxAuthority: string;
  faults: string;
  taxOrganizationName: string;
  phoneNumber: string;
  legalAddress: string;
  email: string;
  filePath: string;
  refreshToken: string;
  logo: string;
  debt: number;
  amount: number;
  isActive: boolean;
  isVatPayer: boolean;
  isRiskyPayer: boolean;
  organizationType: number;
  startDate?: string;
  tokenExpiration?: string;
  paymentAccountId?: number | null;
  paymentAccount?: unknown | null;
  legitimateUserDto?: {
    name: string;
    surname: string;
    fathername: string;
    // Doc 2.1: VÖEN əvvəl yazılıbsa bu sahələr də dolu gəlir (form doldurmaq üçün)
    fiNcode?: string | null;
    email?: string | null;
    position?: string | null;
    department?: number | null;
    isOwner?: boolean | null;
    phonenumbers?: string[] | null;
  } | null;
}

export interface CompanyDocumentItem {
  file: File;
  type: number; // Domain.Enums.CompanyDocumentType
}

export interface AddDocumentsRequest {
  companyId: number;
  files: CompanyDocumentItem[];
  categoryIds?: number[];
  phoneNumber: string;
  email: string;
  logoFile?: File; // Optional logo file
  counterpartyIds?: number[]; // Optional counterparty IDs
}

export interface PhoneNumberDto {
  number: string;
  isWhatsappNumber: boolean;
}

export interface AddLegitimateUserRequest {
  name: string;
  surname: string;
  fathername: string;
  finCode: string;
  email: string;
  department: number;
  position: string;
  isOwner: boolean;
  isActive: boolean;
  companyId: number;
  phonenumbers: PhoneNumberDto[];
}

export interface AddLegitimateUserToCompanyResponse {
  id?: string;
  companyName?: string;
  refreshToken: string | null;
  tokenExpiration: string | null;
  accessToken: {
    token: string;
    expiration: string;
    refreshToken: string;
    refreshTokenLifeTime: number;
  } | null;
  // true olduqda token yoxdur, SİMA təsdiqi tələb olunur
  requiresSimaConfirmation?: boolean;
}

export interface SimaConfirmationStatusResponse {
  voen: string;
  name: string;
  isConfirmed: boolean;
}

export interface AddUserRequest {
  name: string;
  isOwner: boolean;
  email: string;
  companyId: number;
  isActive: boolean;
  fiNcode: string;
  surname: string;
  fathername: string;
  department: number;
  position: string;
  permissionType: number;
}

export interface UpdateCompanyUserRequest {
  id: string;
  name: string;
  surname: string;
  fathername: string;
  department: number;
  position: string;
  fiNcode: string;
  email: string;
  isLegitimateUser: boolean;
  isActive: boolean;
  permissionType: number;
  phonenumbers: {
    isWhatsappNumber: boolean;
    number: string;
  }[];
}

export interface GetCompanyInfoRequest {
  Id: number;
}

export interface GetCompanyInfoResponse {
  id: string;
  name: string;
  voen: string;
  personType: number;
  taxAuthority: string;
  faults: string;
  taxOrganizationName: string;
  phoneNumber: string;
  legalAddress: string;
  email: string;
  filePath: string;
  logo: string;
  debt: number;
  amount: number;
  isActive: boolean;
  isVatPayer: boolean;
  isRiskyPayer: boolean;
  organizationType: number;
  startDate?: string;
  tokenExpiration?: string;
  paymentAccountId?: number | null;
  paymentAccount?: unknown | null;
  legitimateUserDto?: {
    name: string;
    surname: string;
    fathername: string;
  };
  // get-infos cavabında da qeydiyyat statusu gəlir (profil məhdudiyyəti üçün)
  registrationStatus?: CompanyRegistrationStatus;
  // Doc 4.2: dövlət mənbələrindən məlumat (backend qaytararsa profildə göstərilir)
  publicInfo?: PublicInfo;
  // VAT_SIMA: get-infos-da ƏDV rekviziti və SIMA imzalayan
  vatRequisite?: VatRequisite | null;
  simaConfirmation?: SimaConfirmation | null;
}

// get-by-voen (doc 1.6): bütün mərhələlərdə doldurulmuş məlumatlar.
// Backend sahə adları dəqiq bilinmədiyi üçün sahələr boş ötürülə bilər — UI müdafiəli oxuyur.
export interface CompanyByVoenRequisite {
  id?: number;
  account?: string;
  isPrimary?: boolean;
  bankId?: number;
  bankName?: string;
  // Doc 2.6: rekvizitdə "bank" sahəsi (mətn və ya obyekt ola bilər)
  bank?: string | { id?: number; name?: string };
}

export interface CompanyByVoenDocument {
  id?: number;
  type?: number;
  name?: string;
  fileName?: string;
  filePath?: string;
  // Doc 2.6: sənəd birbaşa açılan tam URL (https://…/Company/Documents/…)
  fileUrl?: string;
}

export interface CompanyByVoenCategory {
  id?: number;
  name?: string;
}

export interface CompanyByVoenUser {
  name?: string;
  surname?: string;
  fathername?: string;
  fiNcode?: string;
  email?: string;
  position?: string;
  department?: number;
  isOwner?: boolean;
  isActive?: boolean;
  phones?: Array<{ number?: string; isWhatsappNumber?: boolean } | string>;
}

export interface CompanyByVoenCounterparty {
  id?: number;
  name?: string;
  voen?: string;
  tin?: string;
}

export interface CompanyByVoenResponse {
  id?: number | string;
  name?: string;
  voen?: string;
  legalAddress?: string;
  email?: string;
  phoneNumber?: string;
  taxOrganizationName?: string;
  taxAuthority?: string;
  amount?: number;
  startDate?: string;
  isActive?: boolean;
  isVatPayer?: boolean;
  isRiskyPayer?: boolean;
  logo?: string;
  requisites?: CompanyByVoenRequisite[];
  documents?: CompanyByVoenDocument[];
  categories?: CompanyByVoenCategory[];
  users?: CompanyByVoenUser[];
  companyCounterparties?: CompanyByVoenCounterparty[];
  paymentAccount?: unknown;
  // VAT_SIMA: get-by-voen-da ƏDV rekviziti və SIMA imzalayan
  vatRequisite?: VatRequisite | null;
  simaConfirmation?: SimaConfirmation | null;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  accessToken: {
    token: string;
    expiration: string;
  };
  refreshToken: string;
}

export interface DeleteCompanyUserRequest {
  Id: string; // UUID string
}

export type LogoutRequest = {};

export interface LogoutResponse {
  // Response structure based on API documentation
  success: boolean;
  message: string;
}

export interface ChangeUserStatusRequest {
  id: string; // UUID string
  isActive: boolean;
}

export interface CompanyUpdateDocumentItem {
  file: File;
  type: number;
}

export interface UpdateCompanyRequest {
  id: number;
  phoneNumber: string;
  email: string;
  files?: CompanyUpdateDocumentItem[];
  logoFile?: File;
}

export interface BankRequisiteItem {
  account: string;
  isPrimary: boolean;
  bankId: number;
}

export interface AddRequisitesRequest {
  companyId: number;
  requisites: BankRequisiteItem[];
}

export interface UpdateRequisitesRequest {
  requisites: Array<{
    id: number;
    account: string;
    isPrimary: boolean;
    bankId: number;
  }>;
}

export interface ChangePasswordOtpSent {
  email: string;
  expiresInSeconds: number;
  resendAfterSeconds: number;
}

export interface ChangeCompanyUserPasswordRequest {
  code: string;
  newPassword: string;
  confirmPassword: string;
}

export const companyService = {
  async registerCompany(
    data: RegisterCompanyRequest,
  ): Promise<ApiResponse<RegisterCompanyResponse>> {
    const response = await apiClient.post(
      "/Companies/web/company/register",
      data,
    );
    return response.data;
  },

  // Qeydiyyat təsdiq kodunu yoxla — uğur olarsa saxlanmış məlumatlar + registrationToken gəlir
  async verifyRegistrationCode(data: {
    voen: string;
    code: string;
  }): Promise<ApiResponse<RegisterCompanyResponse>> {
    const response = await apiClient.post(
      "/Companies/web/registration-code/verify",
      data,
    );
    return response.data;
  },

  // Təsdiq kodunu yenidən göndər
  async sendRegistrationCode(
    voen: string,
  ): Promise<ApiResponse<RegistrationVerification>> {
    const response = await apiClient.post(
      "/Companies/web/registration-code/send",
      { voen },
    );
    return response.data;
  },

  async addDocumentsToCompany({
    companyId,
    files,
    categoryIds,
    phoneNumber,
    email,
    logoFile,
    counterpartyIds,
  }: AddDocumentsRequest): Promise<ApiResponse<null>> {
    const formData = new FormData();
    formData.append("CompanyId", String(companyId));
    files.forEach((item, index) => {
      formData.append(`Files[${index}].file`, item.file);
      formData.append(`Files[${index}].type`, String(item.type));
    });
    categoryIds &&
      categoryIds.forEach((categoryId, index) => {
        formData.append(`CategoryIds[${index}]`, String(categoryId));
      });
    formData.append("phoneNumber", phoneNumber);
    formData.append("email", email);

    // Add logo file if provided
    if (logoFile) {
      formData.append("LogoFile", logoFile);
    }

    // Add counterparty IDs if provided
    if (counterpartyIds && counterpartyIds.length > 0) {
      counterpartyIds.forEach((counterpartyId, index) => {
        formData.append(`CounterpartyIds[${index}]`, String(counterpartyId));
      });
    }

    const response = await apiClient.post(
      "/Companies/web/add-documents-to-company",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return response.data;
  },

  async addLegitimateUserToCompany(
    data: AddLegitimateUserRequest,
  ): Promise<ApiResponse<AddLegitimateUserToCompanyResponse>> {
    const response = await apiClient.post(
      "/Companies/web/add-legitimate-user-to-company",
      data,
    );
    return response.data;
  },

  // Anonim endpoint: şirkətin SİMA ilə təsdiqlənib-təsdiqlənmədiyini qaytarır
  async getSimaConfirmationStatus(
    voen: string,
  ): Promise<ApiResponse<SimaConfirmationStatusResponse>> {
    const response = await apiClient.get(
      "/Companies/web/sima-confirmation-status",
      {
        params: { voen },
      },
    );
    return response.data;
  },

  async getCompanyInfo(
    data: GetCompanyInfoRequest,
  ): Promise<ApiResponse<GetCompanyInfoResponse>> {
    const response = await apiClient.get("/Companies/web/get-infos", {
      params: {
        Id: data.Id,
      },
    });
    return response.data;
  },

  // get-by-voen (doc 1.6): bütün mərhələlərdə doldurulmuş məlumatları qaytarır
  async getCompanyByVoen(
    voen: string,
  ): Promise<ApiResponse<CompanyByVoenResponse>> {
    const response = await apiClient.get("/Companies/web/get-by-voen", {
      params: { voen },
    });
    return response.data;
  },

  // Doc 4.1: cari istifadəçinin rolu, şirkət statusu və indi açıq bölmələr
  async getMyAccess(): Promise<ApiResponse<MyAccessResponse>> {
    const response = await apiClient.get(
      "/Companies/web/company-user/my-access",
    );
    return response.data;
  },

  // Doc 4.2: şirkəti hüquqşünas yoxlamasına göndər
  async submitForApproval(): Promise<ApiResponse<null>> {
    const response = await apiClient.post("/Companies/web/approval/submit");
    return response.data;
  },

  // Doc 4.3: təsdiq tarixçəsi (yenisi əvvəldə)
  async getApprovalHistory(): Promise<ApiResponse<ApprovalHistoryItem[]>> {
    const response = await apiClient.get("/Companies/web/approval/history");
    return response.data;
  },

  async refreshToken(
    data: RefreshTokenRequest,
  ): Promise<ApiResponse<RefreshTokenResponse>> {
    const response = await apiClient.post("/Companies/web/refresh-token", data);
    return response.data;
  },

  async addUser(data: AddUserRequest): Promise<ApiResponse<null>> {
    const response = await apiClient.post("/Companies/web/add-user", data);
    return response.data;
  },

  async updateCompanyUser(
    data: UpdateCompanyUserRequest,
  ): Promise<ApiResponse<null>> {
    const response = await apiClient.put(
      "/Companies/web/update-company-user",
      data,
    );
    return response.data;
  },

  async deleteCompanyUser(
    data: DeleteCompanyUserRequest,
  ): Promise<ApiResponse<null>> {
    const response = await apiClient.delete(
      "/Companies/web/delete-company-user",
      {
        params: {
          Id: data.Id,
        },
      },
    );
    return response.data;
  },

  async logout(data: LogoutRequest): Promise<ApiResponse<LogoutResponse>> {
    const response = await apiClient.post(
      "/Companies/web/user-logout-for-company",
      data,
    );
    return response.data;
  },

  async changeUserStatus(
    data: ChangeUserStatusRequest,
  ): Promise<ApiResponse<null>> {
    const response = await apiClient.put(
      "/Companies/web/change-user-status",
      data,
    );
    return response.data;
  },

  async updateCompany({
    id,
    phoneNumber,
    email,
    files,
    logoFile,
  }: UpdateCompanyRequest): Promise<ApiResponse<null>> {
    const formData = new FormData();
    formData.append("Id", String(id));
    formData.append("PhoneNumber", phoneNumber);
    formData.append("Email", email);

    // Add files if provided
    if (files && files.length > 0) {
      files.forEach((item, index) => {
        formData.append(`Files[${index}].file`, item.file);
        formData.append(`Files[${index}].type`, String(item.type));
      });
    }

    // Add logo file if provided
    if (logoFile) {
      formData.append("LogoFile", logoFile);
    }

    const response = await apiClient.put(
      "/Companies/web/update-company",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return response.data;
  },

  async addRequisites(data: AddRequisitesRequest): Promise<ApiResponse<null>> {
    const response = await apiClient.post(
      "/Companies/web/add-requisites",
      data,
    );
    return response.data;
  },

  async updateRequisites(
    data: UpdateRequisitesRequest,
  ): Promise<ApiResponse<null>> {
    const response = await apiClient.put(
      "/Companies/web/update-requisites",
      data,
    );
    return response.data;
  },

  async sendChangePasswordOtp(): Promise<ApiResponse<ChangePasswordOtpSent>> {
    const response = await apiClient.post(
      "/Companies/web/company-user/change-password/send-otp",
    );
    return response.data;
  },

  async changeCompanyUserPassword(
    data: ChangeCompanyUserPasswordRequest,
  ): Promise<ApiResponse<null>> {
    const response = await apiClient.put(
      "/Companies/web/company-user/change-password",
      data,
    );
    return response.data;
  },
};

export default companyService;
